import React, { useEffect, useState } from "react";
import { Col, Image, Toast } from "react-bootstrap";
import Draggable from "react-draggable";
import { Trans, useTranslation } from "react-i18next";
import { NumericFormat as NumberFormat } from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import ContentExpansion from "../../../components/ContentExpansion";
import ConvertToString, { getClassName, isValidNumber } from "../../../components/Converter";
import PlusIcon from "../../../components/Icons/PlusIcon";
import Degree from "../../../components/Math/Units/Metric/Degree";
import KiloNewton from "../../../components/Math/Units/Metric/KiloNewton";
import Metre from "../../../components/Math/Units/Metric/Metre";
import AlertBox from "../../../components/Messages";
import RenderFor, { BM_TYP } from "../../../components/RenderFor";
import { RowBody } from "../../../components/RowValue/RowBody";
import { ResultSpan } from "../../../components/styledComponents/Result";
import ToolTip, { IconType } from "../../../components/ToolTip";
import HiddenWrapperRow from "../../../components/UI/HiddenWrapperRow";
import { decimalSeparator, thousandSeparator } from "../../../i18n";
import zugImage from "../../../images/zug-1.png";
import {
  berechneGewichtkraftDesAngehaengtenBodens,
  berechneLeft,
  berechneLreq,
  berechneRight,
} from "../../../services/berechneZGruppe.service";
import {
  selectAnpassungsfaktor,
  selectAnzahlPfaehle,
  selectBSP,
  selectGroesseresPfahlraster,
  selectKleineresPfahlraster,
  selectPfahllaengeGewaehltInBoden,
  selectReibungswinkelBoden,
  selectStaendigeDestabilisierendeEinwirkungen,
  selectStaendigeStabilisierendeEinwirkungen,
  selectVeraenderlicheDestabilisierendeEinwirkungen,
  selectWichteBoden,
  selectZgruppeLreq,
  setAnpassungsfaktor,
  setAnzahlPfaehle,
  setBSP,
  setGroesseresPfahlraster,
  setKleineresPfahlraster,
  setPfahllaengeGewaehltInBoden,
  setReibungswinkelBoden,
  setStaendigeDestabilisierendeEinwirkungen,
  setStaendigeStabilisierendeEinwirkungen,
  setVeraenderlicheDestabilisierendeEinwirkungen,
  setWichteBoden,
  setZGruppeLReq,
  setZGruppeLReqIsValid,
} from "../../../store/slices/aeussereSlice";
import {
  selectFrontendZustand,
  setKompressionspfahlgruppeDetails,
} from "../../../store/slices/frontendZustandSlice.js";
import {
  BemessungssituationState,
  selectBemessungssituation,
} from "../../../store/slices/innereSlice";
import { GEkFormula, LreqFormula } from "../TeXFormula";

export default function Zugpfahlgruppe() {
  const { t } = useTranslation("translation");
  const dispatch = useDispatch();

  const bemessungsSituation = useSelector(selectBemessungssituation);
  // #region Redux Store Variables
  const wichteBoden = useSelector(selectWichteBoden);
  const anpassungsfaktor = useSelector(selectAnpassungsfaktor);
  //const bsp = useSelector(selectBSP);
  const bsp = berechneBSP(bemessungsSituation);
  const groesseresPfahlraster = useSelector(selectGroesseresPfahlraster);
  const kleineresPfahlraster = useSelector(selectKleineresPfahlraster);
  const reibungswinkelBoden = useSelector(selectReibungswinkelBoden);
  const anzahlPfaehle = useSelector(selectAnzahlPfaehle);
  const { kompressionspfahlgruppenDetails } = useSelector(selectFrontendZustand);
  const pfahllaengeGewaehltInBoden = useSelector(selectPfahllaengeGewaehltInBoden);

  const staendigeDestabilisierendeEinwirkungen = useSelector(
    selectStaendigeDestabilisierendeEinwirkungen
  );
  const veraenderlicheDestabilisierendeEinwirkungen = useSelector(
    selectVeraenderlicheDestabilisierendeEinwirkungen
  );
  const staendigeStabilisierendeEinwirkungen = useSelector(
    selectStaendigeStabilisierendeEinwirkungen
  );
  // #endregion

  const [showZugImage, setShowZugImage] = useState(false);

  // Berechnet
  const Lreq = useSelector(selectZgruppeLreq);

  const [left, setLeft] = useState(0);
  const [right, setRight] = useState(0);
  const [gewichtkraftAngehaengtenBodens, setGewichtkraftAngehaengtenBodens] = useState(0);

  function isLreqValid() {
    if (!isValidNumber(left) || !isValidNumber(right)) {
      return false;
    }
    if (Number(left.toFixed(0)) > Number(right.toFixed(0))) {
      return false;
    }
    return true;
  }

  function berechneBSP(bemessungsSituation) {
    if (bemessungsSituation === BemessungssituationState.DAUERHAFT) {
      return {
        YGD: 1.05,
        YQD: 1.5,
        YGS: 0.95,
      };
    }
    return {
      YGD: 1.05,
      YQD: 1.3,
      YGS: 0.95,
    };
  }
  useEffect(() => {
    dispatch(setBSP(berechneBSP(bemessungsSituation)));
  }, [bemessungsSituation]);
  useEffect(() => {
    //let bsp = berechneBSP(bemessungsSituation);
    let gewichtkraftDesAngehaengtenBodens = berechneGewichtkraftDesAngehaengtenBodens(
      anzahlPfaehle,
      groesseresPfahlraster,
      kleineresPfahlraster,
      pfahllaengeGewaehltInBoden,
      reibungswinkelBoden,
      anpassungsfaktor,
      wichteBoden
    );

    let Lreq_calc = berechneLreq(
      bsp,
      staendigeDestabilisierendeEinwirkungen,
      veraenderlicheDestabilisierendeEinwirkungen,
      staendigeStabilisierendeEinwirkungen,
      kleineresPfahlraster,
      groesseresPfahlraster,
      wichteBoden,
      anpassungsfaktor,
      anzahlPfaehle,
      reibungswinkelBoden
    );

    let left = berechneLeft(
      staendigeDestabilisierendeEinwirkungen,
      bsp,
      veraenderlicheDestabilisierendeEinwirkungen
    );
    let right = berechneRight(
      staendigeStabilisierendeEinwirkungen,
      bsp,
      gewichtkraftDesAngehaengtenBodens
    );

    dispatch(setZGruppeLReq(Lreq_calc));
    setLeft(isValidNumber(left) ? left : 0);
    setRight(isValidNumber(right) ? right : 0);
    let lReqIsValid = left <= right;
    dispatch(setZGruppeLReqIsValid(lReqIsValid));
    setGewichtkraftAngehaengtenBodens(gewichtkraftDesAngehaengtenBodens);

    dispatch(setPfahllaengeGewaehltInBoden(pfahllaengeGewaehltInBoden));
  }, [
    pfahllaengeGewaehltInBoden,
    groesseresPfahlraster,
    kleineresPfahlraster,
    anzahlPfaehle,
    reibungswinkelBoden,
    wichteBoden,
    anpassungsfaktor,
    staendigeDestabilisierendeEinwirkungen,
    veraenderlicheDestabilisierendeEinwirkungen,
    staendigeStabilisierendeEinwirkungen,
  ]);

  return (
    <>
      <div className="row">
        <ContentExpansion
          expand={kompressionspfahlgruppenDetails}
          reducer={setKompressionspfahlgruppeDetails}
        />
      </div>
      <div className="row">
        <div className="col-md-12">
          <h3>{t("aeussre.geometricParametersEarthBody")}</h3>
        </div>

        <RowBody
          titleValue={<p>{t("aeussre.pileLength")}</p>}
          indicesValue={
            <span>
              L<sub>nom</sub>
            </span>
          }
          inputValue={
            <span className="input">{ConvertToString(pfahllaengeGewaehltInBoden, 2)}</span>
          }
          unitValue={<Metre />}
        />
        <RowBody
          titleValue={<p>{t("aeussre.largerGridDimension")}</p>}
          indicesValue={
            <span>
              l<sub>a</sub>
            </span>
          }
          inputValue={
            <NumberFormat
              className={getClassName(groesseresPfahlraster)}
              value={groesseresPfahlraster}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) => dispatch(setGroesseresPfahlraster(value.floatValue))}
              allowNegative={true}
            />
          }
          unitValue={<Metre />}
          formulaValue={
            <span onClick={() => setShowZugImage(!showZugImage)}>
              <PlusIcon />
            </span>
          }
        />
        <HiddenWrapperRow>
          <ShowImageDetails show={showZugImage} setShow={setShowZugImage} image={zugImage} />
        </HiddenWrapperRow>
        <RowBody
          titleValue={<p>{t("aeussre.smallerGridDimension")}</p>}
          indicesValue={
            <span>
              l<sub>b</sub>
            </span>
          }
          inputValue={
            <NumberFormat
              className={getClassName(kleineresPfahlraster)}
              value={kleineresPfahlraster}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) => dispatch(setKleineresPfahlraster(value.floatValue))}
              allowNegative={true}
            />
          }
          unitValue={<Metre />}
        />
        <RowBody
          titleValue={<p>{t("aeussre.internalFrictionAngle")}</p>}
          indicesValue={<span>&phi;</span>}
          inputValue={
            <NumberFormat
              className={getClassName(reibungswinkelBoden)}
              value={reibungswinkelBoden}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) => dispatch(setReibungswinkelBoden(value.floatValue))}
              allowNegative={true}
            />
          }
          unitValue={<Degree />}
        />

        <RowBody
          titleValue={<p>{t("aeussre.soilDensity")}</p>}
          indicesValue={<span>&gamma;</span>}
          inputValue={
            <NumberFormat
              className={getClassName(wichteBoden)}
              value={wichteBoden}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) => dispatch(setWichteBoden(value.floatValue))}
              allowNegative={true}
            />
          }
          unitValue={
            <>
              kN/m<sup>3</sup>
            </>
          }
        />
        <div className="col-md-12">
          <h3>{t("aeussre.impactsPileGroup")}</h3>
        </div>
        <RowBody
          titleValue={<p>{t("aeussre.numberTensionPiles")}</p>}
          indicesValue={
            <span>
              n<sub>z</sub>
            </span>
          }
          inputValue={
            <NumberFormat
              className={getClassName(anzahlPfaehle)}
              value={anzahlPfaehle}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) => dispatch(setAnzahlPfaehle(value.floatValue))}
              allowNegative={true}
            />
          }
          formulaValue={
            <ToolTip
              id={"anzahlPfaehle"}
              type={IconType.INFO}
              text={t("aeussre.numberTensionPiles_info")}
            />
          }
          unitValue={<>{t("aeussre.pieces")}</>}
        />

        <RowBody
          titleValue={<p>{t("aeussre.permanentlyDestabilisingVerticalAction")}</p>}
          indicesValue={
            <span>
              G<sub>dst,k</sub>
            </span>
          }
          inputValue={
            <NumberFormat
              className={getClassName(staendigeDestabilisierendeEinwirkungen)}
              value={staendigeDestabilisierendeEinwirkungen}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) =>
                dispatch(setStaendigeDestabilisierendeEinwirkungen(value.floatValue))
              }
              allowNegative={true}
            />
          }
          unitValue={<KiloNewton />}
        />
        {kompressionspfahlgruppenDetails && (
          <RowBody
            titleValue={<p>{t("aeussre.partialSafetyFactorDestabilisingPermanentActions")}</p>}
            indicesValue={
              <span>
                &gamma;<sub>G,dst</sub>
              </span>
            }
            inputValue={
              <>
                <RenderFor types={[BM_TYP.GERMANY]}>
                  <span className="input">{ConvertToString(bsp?.YGD)}</span>
                </RenderFor>

                <RenderFor types={[BM_TYP.EUROCODE_7]}>
                  <NumberFormat
                    className={getClassName(bsp?.YGD)}
                    value={ConvertToString(bsp?.YGD)}
                    decimalSeparator={decimalSeparator()}
                    thousandSeparator={thousandSeparator()}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    displayType="input"
                    type="text"
                    onValueChange={(value) => {
                      dispatch(
                        setBSP({
                          YGD: value.floatValue,
                        })
                      );
                    }}
                  />
                </RenderFor>
              </>
            }
          />
        )}

        <RowBody
          titleValue={<p>{t("aeussre.destabilisingVariableActions")}</p>}
          indicesValue={
            <span>
              Q<sub>dst,rep</sub>
            </span>
          }
          inputValue={
            <NumberFormat
              className={getClassName(veraenderlicheDestabilisierendeEinwirkungen)}
              value={veraenderlicheDestabilisierendeEinwirkungen}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) =>
                dispatch(setVeraenderlicheDestabilisierendeEinwirkungen(value.floatValue))
              }
              allowNegative={true}
            />
          }
          unitValue={<KiloNewton />}
        />
        {kompressionspfahlgruppenDetails && (
          <RowBody
            titleValue={<p>{t("aeussre.partialSafetyFactorDestabilisingVariableActions")}</p>}
            indicesValue={
              <span>
                &gamma;<sub>Q,dst</sub>
              </span>
            }
            inputValue={
              <>
                <RenderFor types={[BM_TYP.GERMANY]}>
                  <span className="input">{ConvertToString(bsp?.YQD, 2)}</span>
                </RenderFor>

                <RenderFor types={[BM_TYP.EUROCODE_7]}>
                  <NumberFormat
                    className={getClassName(bsp?.YQD)}
                    value={ConvertToString(bsp?.YQD)}
                    decimalSeparator={decimalSeparator()}
                    thousandSeparator={thousandSeparator()}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    displayType="input"
                    type="text"
                    onValueChange={(value) => {
                      dispatch(
                        setBSP({
                          YQD: value.floatValue,
                        })
                      );
                    }}
                  />
                </RenderFor>
              </>
            }
          />
        )}
        <RowBody
          titleValue={<p>{t("aeussre.permanentlyStabilisingVerticalAction")}</p>}
          indicesValue={
            <span>
              G<sub>stb,k</sub>
            </span>
          }
          inputValue={
            <NumberFormat
              className={getClassName(staendigeStabilisierendeEinwirkungen)}
              value={staendigeStabilisierendeEinwirkungen}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) =>
                dispatch(setStaendigeStabilisierendeEinwirkungen(value.floatValue))
              }
              allowNegative={true}
            />
          }
          unitValue={<KiloNewton />}
        />
        {kompressionspfahlgruppenDetails && (
          <>
            <RowBody
              titleValue={<p>{t("aeussre.psfCase1A")}</p>}
              indicesValue={
                <span>
                  &gamma;<sub>G,stb</sub>
                </span>
              }
              inputValue={
                <>
                  <RenderFor types={[BM_TYP.GERMANY]}>
                    <span className="input">{ConvertToString(bsp?.YGS)}</span>
                  </RenderFor>

                  <RenderFor types={[BM_TYP.EUROCODE_7]}>
                    <NumberFormat
                      className={getClassName(bsp?.YGS)}
                      value={ConvertToString(bsp?.YGS)}
                      decimalSeparator={decimalSeparator()}
                      thousandSeparator={thousandSeparator()}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      displayType="input"
                      type="text"
                      onValueChange={(value) => {
                        dispatch(
                          setBSP({
                            YGS: value.floatValue,
                          })
                        );
                      }}
                    />
                  </RenderFor>
                </>
              }
            />

            <RowBody
              titleValue={<p>{t("aeussre.weightSoilAttachedPileGroup")}</p>}
              indicesValue={
                <span>
                  G<sub>E,k</sub>
                </span>
              }
              inputValue={
                <span className="input">{ConvertToString(gewichtkraftAngehaengtenBodens, 0)}</span>
              }
              unitValue={<KiloNewton />}
              formulaValue={
                <ToolTip id={"GEkFormula"} type={IconType.FORMULA} formula={GEkFormula} />
              }
            />
            <RowBody
              titleValue={<p>{t("aeussre.adjustmentFactor")}</p>}
              indicesValue={
                <span>
                  &eta;<sub>z</sub>
                </span>
              }
              inputValue={
                <>
                  <RenderFor types={[BM_TYP.GERMANY]}>
                    <span className="input">{ConvertToString(anpassungsfaktor)}</span>
                  </RenderFor>

                  <RenderFor types={[BM_TYP.EUROCODE_7]}>
                    <NumberFormat
                      className={getClassName(anpassungsfaktor)}
                      value={ConvertToString(anpassungsfaktor)}
                      decimalSeparator={decimalSeparator()}
                      thousandSeparator={thousandSeparator()}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      displayType="input"
                      type="text"
                      onValueChange={(value) => {
                        dispatch(setAnpassungsfaktor(value.floatValue));
                      }}
                    />
                  </RenderFor>
                </>
              }
            />
          </>
        )}

        <div className="col-12">
          <div className="row">
            <div className="col-3 rightstyle">
              <span>
                G<sub>dst,k</sub> * &gamma;<sub>G,dst</sub> + Q<sub>dst,rep</sub> * &gamma;
                <sub>Q,dst</sub>
              </span>
            </div>
            <div className="col-1">
              <span>&le;</span>
            </div>
            <div className="col-3">
              <span>
                G<sub>stb,k</sub> * &gamma;<sub>G,stb</sub> + G<sub>E,k</sub> * &gamma;
                <sub>G,stb</sub>
              </span>
            </div>
          </div>

          <div className="row">
            <div className="col-3 rightstyle">
              <ResultSpan valid={isLreqValid()} className="textAlignmentTable">
                {ConvertToString(left, 0)}
              </ResultSpan>
            </div>
            <div className="col-1">
              <span>&le;</span>
            </div>
            <div className="col-3">
              <ResultSpan valid={isLreqValid()} className="textAlignmentTable">
                {ConvertToString(right, 0)}
              </ResultSpan>
            </div>
            <div className="col-1">
              <span>
                <KiloNewton />
              </span>
            </div>
          </div>
          <Col md={8}>
            <hr />
          </Col>
        </div>

        {Lreq && Number(left.toFixed(0)) > Number(right.toFixed(0)) ? (
          <>
            <Col md={8}>
              <AlertBox
                show={Lreq && Number(left.toFixed(0)) > Number(right.toFixed(0))}
                message={
                  <Trans
                    i18nKey="aeussre.selectLongerPileLnom"
                    values={{
                      lnom: "L<sub>nom</sub>",
                      ge: "≥",
                      lreq: "L<sub>req</sub>",
                    }}
                    components={{ sub: <sub /> }}
                  />
                }
              />
            </Col>
          </>
        ) : null}
        <RowBody
          titleValue={<p>{t("aeussre.pileLengthRequired")}</p>}
          indicesValue={
            <span>
              L<sub>req</sub>
            </span>
          }
          inputValue={<ResultSpan valid={isLreqValid()}>{ConvertToString(Lreq, 2)}</ResultSpan>}
          unitValue={<Metre />}
          formulaValue={
            <ToolTip id={"LreqFormula"} type={IconType.FORMULA} formula={LreqFormula} />
          }
        />
      </div>
    </>
  );
}

function ShowImageDetails({ show, setShow, image }) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable nodeRef={nodeRef}>
      <Toast
        ref={nodeRef}
        onClose={() => setShow(!show)}
        show={show}
        animation={false}
        style={{
          zIndex: 1000,
          backgroundColor: "white",
          position: "absolute",
          width: "auto",
          height: "auto",
        }}
      >
        <Toast.Header className="d-flex flex-row-reverse"></Toast.Header>
        <Toast.Body>
          <Image src={image} width={"300px"} alt="" />
        </Toast.Body>
      </Toast>
    </Draggable>
  );
}
